
/**
 * Module dependencies.
 */

import {
  FormDataProps,
  submitProductContactForm
} from 'src/api/app/forms/submit-product-contact-form';

import { FormGroup, HelpText } from 'src/components/core/forms/fields-styled-components';
import { ProductProps } from 'src/types/products';
import { color, media, units } from 'src/styles/utils';
import { contactSubjectType } from 'src/core/contacts-config';
import { fetchPage } from 'src/api/app/pages/fetch-page';
import { fetchProduct } from 'src/api/app/products/fetch-product';
import { finishesValues } from 'src/core/collections-config/finishes';
import { functions } from 'src/core/collections-config/functions';
import {
  getApiPageSlugByLocale,
  resolvePageUrl,
  useLocalizedRoute
} from 'src/core/utils/routes';

import { getKeyFromObject } from 'src/core/utils/objects';
import { ifProp, theme } from 'styled-tools';
import { regexes } from 'src/core/utils/regexes';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation } from 'react-query';
import { useNewsletter } from 'src/api/app/forms/newsletter/use-newsletter';
import { useRouter } from 'next/router';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import CardTitle from 'src/components/core/typography/card-title';
import Checkbox from 'src/components/core/checkbox';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import Input from 'src/components/core/forms/fields/input';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import Navigation from 'src/components/navigation';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import PrivacyPolicyCheckboxLabel from 'src/components/core/forms/privacy-policy-checkbox-label';
import RawHtml from 'src/components/core/raw-html';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import Select from 'src/components/core/forms/fields/select';
import Type from 'src/components/core/typography';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useCountries from 'src/api/app/countries/use-countries';
import useDistricts from 'src/api/app/countries/use-districts';
import useGTMEvent from 'src/hooks/use-gtm-event';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usePage from 'src/api/app/pages/use-page';
import useProduct from 'src/api/app/products/use-product';
import useShowrooms from 'src/api/app/showrooms/use-showrooms';

/**
 * `PageProps` type.
 */

type PageProps = {
  id: string,
  slug: string,
  subtitle: string,
  title: string
};

/**
 * `Props` type.
 */

type Props = {
  data: PageProps,
  product: ProductProps,
  slug: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ isLoading: boolean }>`
  background-color: ${color('beige200')};
  padding-bottom: ${units(13)};
  padding-top: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(7)});
  position: relative;

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}

  ${media.min('ms')`
    padding-bottom: ${units(25)};
    padding-top: calc(${theme('dimensions.navbarHeight')}px + ${units(10)});
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'navigation navigation'
    'title      title'
    'details    .'
    'form       form';
  grid-template-columns: repeat(2, 1fr);

  ${media.min('ms')`
    grid-template-areas:
      '. navigation navigation . .'
      '. title      title      title .'
      '. details    .          form  .';
    grid-template-columns: 1fr 4fr 1fr 4fr 2fr;
  `}
`;

/**
 * `StyledNavigation` styled component.
 */

const StyledNavigation = styled(Navigation)`
  grid-area: navigation;
  margin-bottom: ${units(5.5)};

  ${media.min('ms')`
    padding-left: ${units(3.5)};
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  grid-area: title;
  margin-bottom: ${units(3)};

  ${media.min('ms')`
    margin-bottom: ${units(10)};
  `}
`;

/**
 * `Subtitle` styled component.
 */

const Subtitle = styled(Type.H6).attrs({ as: 'p', small: true })`
  color: ${color('grey400')};
`;

/**
 * `DetailsWrapper` styled component.
 */

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: details;
  margin-bottom: ${units(6)};
  overflow: hidden;
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  background-color: ${color('beige100')};
  padding-bottom: 100%;
  position: relative;

  > div {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

/**
 * `ProductName` styled component.
 */

const ProductName = styled(CardTitle).attrs({ size: 'small' })`
  margin-bottom: ${units(1)};
  padding-top: ${units(1)};
`;

/**
 * `Label` styled component.
 */

const Label = styled(Type.Small).attrs({
  as: 'p',
  small: true
})<{ uppercase?: boolean }>`
  color: ${color('grey300')};

  ${ifProp('uppercase', css`
    text-transform: uppercase;
  `)}
`;

/**
 * `Form` styled component.
 */

const Form = styled.form`
  grid-area: form;
`;

/**
* `StyledFormGroup` styled component.
*/

const StyledFormGroup = styled(FormGroup)`
  margin-bottom: ${units(2)};
`;

/**
 * `StyledCheckbox` styled component.
 */

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: ${units(2)};

  &:last-of-type {
    margin-bottom: ${units(6)};
  }
`;

/**
 * `ProductContact` page.
 */

const ProductContact = (props: Props): ReactElement => {
  const { showMessage } = useSnackbar();
  const { locale, translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const { query } = useRouter();
  const {
    data: pageData,
    isLoading: isLoadingPage,
    isSuccess: isSuccessPage
  } = usePage<PageProps>({
    initialData: props.data,
    slug: props.slug
  });

  const {
    data: productData,
    isLoading: isLoadingProduct,
    isSuccess: isSuccessProduct
  } = useProduct<ProductProps>({
    initialData: props.product,
    slug: get(query, 'slug')
  });

  const {
    data: countriesData,
    isLoading: isLoadingCountries
  } = useCountries();

  const {
    data: districtsData,
    isLoading: isLoadingDistricts
  } = useDistricts();

  const {
    data: showrooms,
    isLoading: isLoadingShowrooms
  } = useShowrooms();

  const subjectOptions = Object
    .entries(contactSubjectType)
    .map(([key, value]) => ({
      label: translate(`forms:subjects.${key}`),
      value
    }));

  const isLoading = isLoadingProduct || isLoadingPage;
  const isSuccess = isSuccessProduct && isSuccessPage;
  const imageUrl = productData?.media ? last(productData.media).fileUrl : null;
  const finishKey = getKeyFromObject(finishesValues, productData?.finish);
  const functionKey = getKeyFromObject(functions, productData?.function);
  const format = productData?.format;
  const formatApproximatedValue = useMemo(() => {
    return format?.approximated ? `[ ${format.approximated.split('.').join(',')} cm ]` : '';
  }, [format]);

  const {
    control,
    errors,
    formState,
    handleSubmit,
    register,
    reset,
    watch
  } = useForm({ mode: 'onBlur' });

  const sectionRef = useRef<HTMLDivElement>();
  const datetimeRef = useRef<HTMLInputElement>();
  const [isSubmitted, setIsSubmitted] = useState<boolean>();
  const watchCountry = watch('countryAlpha2', 'PT');
  const watchSubject = watch('subject', 'sample request');
  const sendGTMEvent = useGTMEvent();
  const {
    isLoading: isLoadingSubmitForm,
    mutateAsync: submitForm
  } = useMutation((formData: FormDataProps) => {
    return submitProductContactForm({ formData, locale });
  }, {
    onError: () => {
      showMessage(translate('forms:messages.error'), { appearance: 'error' });
    },
    onSuccess: () => {
      showMessage(translate('forms:messages.success'), { appearance: 'success' });

      setIsSubmitted(true);
      reset();
      sendGTMEvent('contact', resolvePageUrl('contacts', locale));

      // Manual reset of fields that use Controller.
      reset({
        countryAlpha2: '',
        districtId: '',
        subject: ''
      });
    }
  });

  const {
    isSubmitting: isSubscribing,
    onSubscribeNewsletter
  } = useNewsletter({ hideMessages: true });

  const isSubmitting = (isLoadingSubmitForm ?? formState?.isSubmitting) || isSubscribing;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const onSubmit = useCallback(async data => {
    const {
      company,
      countryAlpha2,
      dateTime,
      districtId,
      email,
      isIndustryProfessional,
      name,
      observations,
      phone,
      place,
      showroom,
      subject,
      subscribeNewsletter
    } = data;

    let recaptchaToken = await executeRecaptcha('productContactForm').then(token => token);
    const normalizedData = {
      countryAlpha2: countryAlpha2?.value,
      email,
      isIndustryProfessional,
      name,
      productId: productData?.id,
      recaptchaToken,
      subject: subject?.value,
      ...!isEmpty(company) ? { company } : {},
      ...!isEmpty(phone) ? { phone } : {},
      ...countryAlpha2?.value === 'PT' ? { districtId: districtId?.value } : { place },
      ...subject?.value === contactSubjectType.visitScheduling && {
        dateTime,
        showroomId: showroom?.value
      },
      ...subject?.value === contactSubjectType.requestForInformation && { observations }
    };

    submitForm(normalizedData).then(async () => {
      if (subscribeNewsletter) {
        recaptchaToken = await executeRecaptcha('contactForm').then(token => token);

        onSubscribeNewsletter({
          countryAlpha2: countryAlpha2?.value,
          email,
          name,
          professional: !!isIndustryProfessional,
          recaptchaToken
        });
      }
    });
  }, [executeRecaptcha, onSubscribeNewsletter, productData.id, submitForm]);

  useNavbarTheme({ theme: 'dark' });

  useEffect(() => {
    if (!isSubmitted) {
      return;
    }

    const timeout = setTimeout(() => {
      if (sectionRef.current && sectionRef.current.scrollIntoView) {
        sectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }

      setIsSubmitted(false);
    }, 50);

    return () => clearTimeout(timeout);
  }, [isSubmitted]);

  return (
    <>
      <Metatags
        canonicalUrl={resolvePageUrl('contacts', locale)}
        description={productData?.descriptionSeo}
        isProductContact
        pageTitle={pageData?.title}
      />

      <Section
        isLoading={isLoading}
        ref={sectionRef}
      >
        <Loading active={isLoading} />

        {isSuccess && (
          <Container>
            <Grid>
              <StyledNavigation
                href={routeResolver('productDetails', { slug: productData?.slug })}
                label={translate('products:actions.viewProduct')}
              />

              <TitleWrapper>
                <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                  <Type.H1>
                    {pageData?.title}
                  </Type.H1>

                  <Subtitle>
                    {pageData?.subtitle}
                  </Subtitle>
                </ParallaxFadeInUp>
              </TitleWrapper>

              <DetailsWrapper>
                <ParallaxFadeInUp
                  parallaxOptions={{
                    percentage: 0.1,
                    speed: 2
                  }}
                >
                  <ImageWrapper>
                    {imageUrl && (
                      <Image
                        alt={pageData?.title}
                        layout={'fill'}
                        objectFit={'scale-down'}
                        objectPosition={'center'}
                        src={imageUrl}
                      />
                    )}
                  </ImageWrapper>
                </ParallaxFadeInUp>

                <ParallaxFadeInUp
                  parallaxOptions={{
                    percentage: 0.1,
                    speed: 2
                  }}
                >
                  <ProductName>
                    {productData?.name}
                  </ProductName>

                  {format && (
                    <Label>
                      {`${format.name} ${formatApproximatedValue}`}
                    </Label>
                  )}

                  {finishKey && (
                    <Label uppercase>
                      {translate(`collections:finishes.${finishKey}`)}
                    </Label>
                  )}

                  {functionKey && (
                    <Label>
                      {translate(`products:functions.${functionKey}`)}
                    </Label>
                  )}
                </ParallaxFadeInUp>
              </DetailsWrapper>

              <Form onSubmit={handleSubmit(onSubmit)}>
                <FadeInUpAnimation>
                  <Input
                    disabled={isSubmitting}
                    error={errors?.name}
                    id={'product-contact-form-name'}
                    label={translate('forms:nameLabel')}
                    name={'name'}
                    placeholder={translate('forms:namePlaceholder')}
                    ref={register<any>({
                      required: {
                        message: translate('forms:requiredField'),
                        value: true
                      }
                    })}
                  />

                  <Input
                    disabled={isSubmitting}
                    error={errors?.email}
                    id={'product-contact-form-email'}
                    label={translate('forms:emailLabel')}
                    name={'email'}
                    placeholder={translate('forms:emailPlaceholder')}
                    ref={register<any>({
                      pattern: {
                        message: translate('forms:emailPatternValidation'),
                        value: regexes.email
                      },
                      required: {
                        message: translate('forms:requiredField'),
                        value: true
                      }
                    })}
                    type={'email'}
                  />

                  <Input
                    disabled={isSubmitting}
                    error={errors?.phone}
                    label={translate('forms:phoneNumberLabel')}
                    name={'phone'}
                    placeholder={translate('forms:phoneNumberPlaceholder')}
                    ref={register}
                  />

                  <Input
                    disabled={isSubmitting}
                    error={errors?.company}
                    label={translate('forms:companyNameLabel')}
                    name={'company'}
                    placeholder={translate('forms:companyNamePlaceholder')}
                    ref={register}
                  />

                  <Select
                    control={control}
                    disabled={isSubmitting}
                    error={errors?.countryAlpha2}
                    id={'product-contact-form-country'}
                    isLoading={isLoadingCountries}
                    label={translate('forms:countryLabel')}
                    name={'countryAlpha2'}
                    options={map(countriesData, ({ alpha2, name }) => ({
                      label: name,
                      value: alpha2
                    }))}
                    placeholder={translate('forms:selectPlaceholder')}
                    rules={{ required: {
                      message: translate('forms:requiredField'),
                      value: true
                    } }}
                  />

                  {watchCountry.value === 'PT' ? (
                    <Select
                      control={control}
                      disabled={isSubmitting}
                      error={errors?.districtId}
                      isLoading={isLoadingDistricts}
                      label={translate('forms:districtLabel')}
                      name={'districtId'}
                      options={map(districtsData, ({ id, name }) => ({
                        label: name,
                        value: id
                      }))}
                      placeholder={translate('forms:selectPlaceholder')}
                      rules={{ required: {
                        message: translate('forms:requiredField'),
                        value: true
                      } }}
                    />
                  ) : (
                    <Input
                      disabled={isSubmitting}
                      error={errors?.place}
                      label={translate('forms:locationLabel')}
                      name={'place'}
                      placeholder={translate('forms:locationPlaceholder')}
                      ref={register<any>({
                        required: {
                          message: translate('forms:requiredField'),
                          value: true
                        }
                      })}
                    />
                  )}

                  <Select
                    control={control}
                    disabled={isSubmitting}
                    error={errors?.subject}
                    label={translate('forms:subjectLabel')}
                    name={'subject'}
                    options={subjectOptions}
                    placeholder={translate('forms:subjectPlaceholder')}
                    rules={{ required: {
                      message: translate('forms:requiredField'),
                      value: true
                    } }}
                  />

                  {watchSubject.value === contactSubjectType.visitScheduling && (
                    <>
                      <Select
                        control={control}
                        disabled={isSubmitting}
                        error={errors?.showroom}
                        isLoading={isLoadingShowrooms}
                        label={translate('forms:showroomLabel')}
                        name={'showroom'}
                        options={map(showrooms, ({ id, name }) => ({
                          label: name,
                          value: id
                        }))}
                        placeholder={translate('forms:selectPlaceholder')}
                        rules={{ required: {
                          message: translate('forms:requiredField'),
                          value: true
                        } }}
                      />

                      <Input
                        disabled={isSubmitting}
                        error={errors?.dateTime}
                        label={translate('forms:dateTimeLabel')}
                        name={'dateTime'}
                        onBlur={() => {
                          if (!datetimeRef.current.value) {
                            datetimeRef.current.type = 'text';
                          }
                        }}
                        onFocus={() => {
                          datetimeRef.current.type = 'datetime-local';
                        }}
                        placeholder={translate('forms:selectPlaceholder')}
                        ref={element => {
                        // @ts-ignore
                          datetimeRef.current = element;
                          register<any>(element, {
                            required: {
                              message: translate('forms:requiredField'),
                              value: true
                            }
                          });
                        }}
                        type={'text'}
                      />
                    </>
                  )}

                  {watchSubject.value === contactSubjectType.requestForInformation && (
                    <Input
                      as={'textarea'}
                      disabled={isSubmitting}
                      error={errors?.observations}
                      label={translate('forms:observationsLabel')}
                      name={'observations'}
                      placeholder={translate('forms:observationsPlaceholder')}
                      ref={register}
                      rows={4}
                    />
                  )}

                  <StyledCheckbox
                    alignment={'flex-start'}
                    disabled={isSubmitting}
                    id={'product-contact-form-construction-sector'}
                    label={
                      <RawHtml>
                        {translate('forms:constructionSectorCheckbox')}
                      </RawHtml>
                    }
                    name={'isIndustryProfessional'}
                    ref={register}
                  />

                  <StyledFormGroup>
                    <Checkbox
                      alignment={'flex-start'}
                      disabled={isSubmitting}
                      id={'product-contact-form-privacy-policy'}
                      label={
                        <PrivacyPolicyCheckboxLabel
                          i18nKey={'forms:privacyPolicyContactsCheckbox'}
                        />
                      }
                      name={'acceptPrivacyPolicy'}
                      ref={register<any>({
                        required: {
                          message: translate('forms:privacyPolicyError'),
                          value: true
                        }
                      })}
                    />

                    {errors?.acceptPrivacyPolicy && (
                      <HelpText hasError>
                        {errors?.acceptPrivacyPolicy.message}
                      </HelpText>
                    )}
                  </StyledFormGroup>

                  <StyledCheckbox
                    alignment={'flex-start'}
                    disabled={isSubmitting}
                    id={'product-contact-form-newsletter'}
                    label={translate('forms:newsletterCheckbox')}
                    name={'subscribeNewsletter'}
                    ref={register}
                  />

                  <Button
                    colorTheme={'primary'}
                    isLoading={isSubmitting}
                    type={'submit'}
                    variant={'fill'}
                  >
                    {translate('forms:submit')}
                  </Button>
                </FadeInUpAnimation>
              </Form>
            </Grid>
          </Container>
        )}
      </Section>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export async function getServerSideProps({ locale, params, query }) {
  const slug = getApiPageSlugByLocale('productContact', locale);
  const product = await fetchProduct({ locale, slug: params?.slug });
  const pageData = await fetchPage({
    hasPreview: query?.preview,
    locale,
    slug
  });

  if (!pageData || !product) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      data: pageData,
      product,
      slug
    }
  };
}

/**
 * Export `ProductContact` page.
 */

export default ProductContact;
